/**
 * Estado do rastreio da chuva (Zustand). Registra o codec de URL `i` no
 * import — `ui/modules.tsx` importa este arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { IndiceChuva } from './api';

/** Começa desligado, como as outras camadas extras. */
export const DEFAULT_LIGADO = false;

export type EstadoCarga = 'idle' | 'loading' | 'ready' | 'error';

/**
 * O rastreio no quadro exibido:
 * `ok` na tela · `carregando` baixando (o mapa fica só com a imagem) ·
 * `sem` nenhum horário do GSMaP casa com esta imagem · `falhou` o arquivo não veio.
 */
export type EstadoNoQuadro = 'ok' | 'carregando' | 'sem' | 'falhou';

/** A história de uma área de chuva até o quadro: vetores do mesmo tamanho, `dt` crescente, o último é o próprio quadro (0). */
export interface HistoricoChuva {
  dt: number[];
  km2: (number | null)[];
  pmax: (number | null)[];
  pmed: (number | null)[];
}

/** Uma área de chuva rastreada, como a ficha a recebe. */
export interface SistemaChuva {
  uid: number | null;
  /** Situação do pyForTraCC: NEW, CON, SPL, MRG… */
  st: string | null;
  /** Tempo de vida (min). */
  vida: number | null;
  km2: number | null;
  /** Taxa máxima e média (mm/h), e o desvio-padrão. */
  pmax: number | null;
  pmed: number | null;
  dp: number | null;
  /** Expansão normalizada da área (10⁻⁶ s⁻¹). */
  exp: number | null;
  vel: number | null;
  rumo: number | null;
  hist: HistoricoChuva | null;
  /** Início e fim da hora do GSMaP (epoch ms) e o meio, que casa com a imagem. */
  ini: number;
  fim: number;
  t: number;
}

export interface SistemaChuvaNaLista {
  sistema: SistemaChuva;
  /** Ponto dentro do contorno e retângulo dele (projeção do mapa). */
  ponto: [number, number];
  extensao: [number, number, number, number];
}

export interface RastreioChuvaState {
  ligado: boolean;
  situacao: { estado: EstadoCarga; erro: string | null };
  indice: IndiceChuva | null;
  noQuadro: EstadoNoQuadro;
  /** Áreas do quadro na tela, da maior para a menor. */
  sistemas: SistemaChuvaNaLista[];
  /** `uid` da ficha aberta: destacado no mapa e na lista. Não vai para o link. */
  selecao: number | null;
  /** Lista do painel expandida, e inteira. Valem enquanto a página está aberta. */
  listaAberta: boolean;
  listaInteira: boolean;

  setLigado: (on: boolean) => void;
  setSelecao: (uid: number | null) => void;
  setListaAberta: (aberta: boolean) => void;
  setListaInteira: (inteira: boolean) => void;
}

export const useRastreioChuvaStore = create<RastreioChuvaState>()((set) => ({
  ligado: DEFAULT_LIGADO,
  situacao: { estado: 'idle', erro: null },
  indice: null,
  noQuadro: 'sem',
  sistemas: [],
  selecao: null,
  listaAberta: false,
  listaInteira: false,
  setLigado: (ligado) => set(ligado ? { ligado } : { ligado, selecao: null }),
  setSelecao: (selecao) => set({ selecao }),
  setListaAberta: (listaAberta) => set({ listaAberta }),
  setListaInteira: (listaInteira) => set({ listaInteira }),
}));

/** `i=1` liga. Desligado (o padrão), fica fora do link. */
export function encodeRastreioChuva(s: Pick<RastreioChuvaState, 'ligado'>): string | null {
  return s.ligado ? '1' : null;
}

export function decodeRastreioChuva(value: string): Partial<Pick<RastreioChuvaState, 'ligado'>> | null {
  if (value === '1') return { ligado: true };
  if (value === '0') return { ligado: false };
  return null;
}

registerUrlCodec({
  key: 'i',
  encode: () => encodeRastreioChuva(useRastreioChuvaStore.getState()),
  decode: (value) => {
    const s = decodeRastreioChuva(value);
    if (s) useRastreioChuvaStore.setState(s);
  },
  subscribe: (onChange) =>
    useRastreioChuvaStore.subscribe((a, b) => {
      if (a.ligado !== b.ligado) onChange();
    }),
});
